/**
 * Login.js — Login modal with two-factor authentication.
 *
 * Step 1: User enters email and password. On success the engine sends a 2FA code.
 * Step 2: User enters the code within a 5-minute countdown. On success, DoLogin is called.
 *
 * Also handles the "Forgot Email/Password" flow (shown as separate sub-steps).
 *
 * Props:
 *  - LoginPopup {boolean}                     Whether the modal is visible.
 *  - HideLoginPopup {Function}                Closes the modal.
 *  - EngineURL {string}                       Base URL for the engine API.
 *  - DoLogin {Function}                       Called with user details after successful login.
 *  - OpenRegisterPopupFromLogin {Function}    Switches from login to create-account modal.
 *  - setLoginErrorMessage {Function}          Updates the login error message in App state.
 *  - LoginErrorMsgm {string|undefined}        Current login error message.
 *  - useLoggedIn {boolean}                    Whether the user is already logged in.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useEffect } from "react";
import {
  // Card,
  Button,
  Modal,
  Alert,
  Form,
} from "react-bootstrap";
import { useLocation } from "react-router";
import { isValidPhoneNumber } from "react-phone-number-input";
import axios from "axios";

export const Login = (props) => {
  const location = useLocation();
  //console.log("Login ->");
  //console.log(location);

  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserpassword] = useState("");
  const [step, setStep] = useState(1);
  const [seconds, setSeconds] = useState(299);
  const [canResend, setCanResend] = useState(false);
  const [SendingCodeInProgress, setSendingCodeInProgress] = useState(false);

  useEffect(() => {
    let timer;
    if (seconds > 0) {
      timer = setTimeout(() => setSeconds((prev) => prev - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearTimeout(timer);
  }, [seconds]);

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const secs = time % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };


  /***
   * Error Handling
   */
  const [userEmailerrror, setUserEmailerror] = useState(false);
  const [userPasswordError, setUserpasswordError] = useState(false);

  const [SuccessMessage, setSuccessMessage] = useState(undefined);
  const [ErrorMessage, setErrorMessage] = useState(undefined);
  const [SomethingUpadating, setSomethingUpadating] = useState(false);

  /**
   * On close, clear ONLY the validation / error state so reopening doesn't show
   * stale errors. The OTP step, the typed email/code, and the countdown timer
   * are intentionally preserved so a user can close mid-2FA and reopen with the
   * timer still running.
   */
  useEffect(() => {
    if (!props.LoginPopup) {
      setUserEmailerror(false);
      setUserpasswordError(false);
      setErrorMessage(undefined);
      setSuccessMessage(undefined);
      setSomethingUpadating(false);
      props.setLoginErrorMessage(undefined);
    }
  }, [props.LoginPopup]);

  const validateEmail = (email) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase()) || isValidPhoneNumber(email);
  };
  const RestVariableBeforeFormSubmit = () => {
    setUserEmailerror(false);
    setErrorMessage(undefined);
    props.setLoginErrorMessage(undefined);
    setSomethingUpadating(false);
    setSuccessMessage(undefined);
  };

  // Step 1: Submit email

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    RestVariableBeforeFormSubmit();

    var emailValid = true;

    if (!userEmail) {
      setUserEmailerror(true);
      setUserpasswordError(true);
      emailValid = false;
    }

    if (!validateEmail(userEmail)) {
      setUserEmailerror(true);
      emailValid = false;
    }

    if (emailValid) {
      setSomethingUpadating(true);

      // * Temporary fix */
      // props.DoLogin(
      //   'sureshsuthar2011@gmail.com',
      //   'sdfsdfdsf',
      //   'suresh',
      //   'suthar',
      //   '+919724790448',
      //   null
      // );


      //do login with mobile number
      const checkLogin = isValidPhoneNumber(userEmail)
        ? await axios.post(props.EngineURL + "create-account/2fa/create_2fa.php", {
          phone: userEmail.replace(/\s/g, ""),
        })
        : await axios.post(props.EngineURL + "create-account/2fa/create_2fa.php", {
          email: userEmail.replace(/\s/g, ""),
        });

      if (checkLogin.status === 200 && checkLogin.data.code === 200) {
        setStep(2);
        setSeconds(299); // Reset timer
        setCanResend(false); // Disable button again
        setSomethingUpadating(false);
        //  console.log(response.data.email);
      } else {
        setErrorMessage(checkLogin.data.message);
        setSomethingUpadating(false);
      }
    }
    // Client-side validation failures are shown via the inline field message
    // (userEmailerrror) only — no separate "Fields are required!" alert.
  };

  useEffect(() => {
    if (props.LoginErrorMsgm !== undefined) {
      setErrorMessage(props.LoginErrorMsgm);
      setSomethingUpadating(false);
    }
  }, [props.LoginErrorMsgm]);

  //Validate code and submit
  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    RestVariableBeforeFormSubmit();

    var passwordValid = true;

    if (!userPassword) {
      setUserpasswordError(true);
      passwordValid = false;
    }

    if (passwordValid) {
      setSomethingUpadating(true);
      try {
        const faValidationResponse = isValidPhoneNumber(userEmail)
          ? await axios.post(props.EngineURL + "create-account/2fa/validate_2fa.php", {
            phone: userEmail.replace(/\s/g, ""),
            auth_code: userPassword,
          })
          : await axios.post(props.EngineURL + "create-account/2fa/validate_2fa.php", {
            email: userEmail.replace(/\s/g, ""),
            auth_code: userPassword,
          });

        if (faValidationResponse.status === 200 && faValidationResponse.data.code === 200) {
          console.log("Login Success");
          props.DoLogin(
            faValidationResponse.data.email,
            faValidationResponse.data.access_token,
            faValidationResponse.data.first_name,
            faValidationResponse.data.last_name,
            faValidationResponse.data.mobile,
            location.pathname
          );
          setSuccessMessage(faValidationResponse.data.message);
          setSomethingUpadating(false);
        } else {
          setErrorMessage(faValidationResponse.data.message);
          setSomethingUpadating(false);
        }
      } catch (error) {
        console.error("Error during 2FA validation:", error);
      }
    }
    // Empty code is shown via the inline field message (userPasswordError) only.
  };

  const handleResend = async () => {
    // Trigger resend logic here (API call etc.)
    console.log("Code resent");
    // setSomethingUpadating(true);
    RestVariableBeforeFormSubmit();
    setSendingCodeInProgress(true);
    try {
      const responseReset = isValidPhoneNumber(userEmail)
        ? await axios.post(props.EngineURL + "create-account/2fa/reset_2fa.php", {
          phone: userEmail.replace(/\s/g, ""),
        })
        : await axios.post(props.EngineURL + "create-account/2fa/reset_2fa.php", {
          email: userEmail.replace(/\s/g, ""),
        });

      if (responseReset.status === 200 && responseReset.data.code === 200) {
        setSeconds(20); // Reset timer
        setCanResend(false); // Disable button again
        setSendingCodeInProgress(false);
      } else {
        setErrorMessage(responseReset.data.message);
        setSendingCodeInProgress(false);
      }
      // setSomethingUpadating(false);
    } catch (error) {
      console.error("Error during code resend:", error);
      setErrorMessage("Failed to resend code. Please try again.");
      setSendingCodeInProgress(false);
    }
  };

  return (
    <>
      <Modal className="login_modal" show={props.LoginPopup} onHide={props.HideLoginPopup} backdrop="static" keyboard={false}>
        <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
        <Modal.Body className="pt-0">
          <Modal.Title>Returning Customers</Modal.Title>
          {step === 1 && (
            <Form className="" onSubmit={handleEmailSubmit}>
              <Form.Group className="mb-4" controlId="LoginEmail">
                <Form.Control
                  className={userEmailerrror ? "form-error" : ""}
                  type="text"
                  onChange={(e) => { setUserEmail(e.target.value); if (userEmailerrror) setUserEmailerror(false); }}
                  value={userEmail}
                  placeholder="Enter email or Mobile number"
                />
                {userEmailerrror && <span className="field-error-msg">Please enter a valid email or mobile number</span>}
                <small className="form-check-label-small">Mobile number format e.g +61 000000000</small>
              </Form.Group>

              <div className="responseMessage">
                {ErrorMessage !== undefined ? <Alert variant="danger">{ErrorMessage}</Alert> : ""}

                {SuccessMessage !== undefined ? <Alert variant="success">{SuccessMessage}</Alert> : ""}
                {SomethingUpadating === true ? <Alert variant="primary">Validating...</Alert> : ""}
              </div>
              <Button variant="primary" type="submit" className="w-100 rounded-2 btn-lg border-2" disabled={SomethingUpadating === true}>
                send verification code
              </Button>
              <p className="mt-4">
                Don't have an account?{" "}
                <span onClick={() => props.OpenRegisterPopupFromLogin()} className="text-secondary" style={{ cursor: 'pointer' }}>
                  Sign Up
                </span>
              </p>
            </Form>
          )}
          {step === 2 && (
            <Form className="" onSubmit={handleCodeSubmit}>
              <p>Check your inbox for your one-time code. We’ve sent it to your registered email.</p>
              <Form.Group className="mb-4" controlId="LoginPassword">
                <Form.Control
                  className={userPasswordError ? "form-error" : ""}
                  type="text"
                  onChange={(e) => { setUserpassword(e.target.value); if (userPasswordError) setUserpasswordError(false); }}
                  value={userPassword}
                  placeholder="Code"
                />
                {userPasswordError && <span className="field-error-msg">Please enter the verification code</span>}
              </Form.Group>
              <div className="mb-2">
                {SendingCodeInProgress ? (
                  <Alert variant="primary">Resending...</Alert>
                ) : canResend ? (
                  <span className="text-secondary" onClick={handleResend}>
                    Resend Code
                  </span>
                ) : (
                  <><p>Resend code in {formatTime(seconds)}</p>

                  </>
                )}
              </div>
              <div className="responseMessage">
                {ErrorMessage !== undefined ? <Alert variant="danger">{ErrorMessage}</Alert> : ""}

                {SuccessMessage !== undefined ? <Alert variant="success">{SuccessMessage}</Alert> : ""}
                {SomethingUpadating ? <Alert variant="primary">Validating...</Alert> : ""}
              </div>
              <Button variant="primary" type="submit" className="w-100 rounded-2 btn-lg border-2" disabled={SomethingUpadating === true}>
                Submit
              </Button>
              <p className="mt-4">Didn’t get the email? Check your spam folder or request a new code.</p>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
